import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import { getCsrfToken } from '../../Shared/API/config';

export type ReceiptBalanceDirection = 'owed' | 'credit' | 'settled';

type ReceiptStudent = {
  name: string;
  /** May be empty when the student has no current class. */
  className: string;
};

export type PaidReceipt = {
  status: 'paid';
  receiptNumber: string;
  amount: string;
  paymentDate: string;
  paymentMethod: string | null;
  paidBy: string | null;
  student: ReceiptStudent;
  /** Frozen at the time of payment; null on payments recorded before balances were frozen. */
  balanceAfter: { amount: string; direction: ReceiptBalanceDirection } | null;
};

export type VoidReceipt = {
  status: 'void';
  receiptNumber: string;
  amount: string;
  paymentDate: string;
  student: ReceiptStudent;
};

export type PublicReceipt = PaidReceipt | VoidReceipt;

type ReceiptTokenArgs = { token: string };

const fetchReceipt = fetchBaseQuery({
  baseUrl: '/api',
  prepareHeaders: headers => {
    const csrfToken = getCsrfToken();
    if (csrfToken) headers.set('X-CSRFToken', csrfToken);
    return headers;
  },
});

/**
 * A deliberately quiet base query for the public receipt page.
 *
 * The shared `baseQuery` console-logs every failed request's args — here that
 * is `{ token }`, which Sentry would then lift into a breadcrumb — and reports
 * expected 4xx (an unknown link is a 404) as issues. This one logs nothing,
 * sends no Authorization header, and only reports server faults, by path.
 */
const quietBaseQuery: typeof fetchReceipt = async (args, api, extraOptions) => {
  const result = await fetchReceipt(args, api, extraOptions);
  const status = result.error?.status;
  if (result.error && (typeof status !== 'number' || status >= 500)) {
    const path = typeof args === 'string' ? args : args.url;
    Sentry.captureException(new Error(`API ${status} POST /api${path}`), {
      level: 'error',
      fingerprint: ['api-error', 'POST', path, String(status)],
      tags: { 'api.status': String(status), 'api.path': path, app: 'public-receipt' },
    });
  }
  return result;
};

export const PublicReceiptApi = createApi({
  reducerPath: 'PublicReceiptApi',
  baseQuery: quietBaseQuery,
  endpoints: builder => ({
    // POST so the token travels in the body — never in a path, a log line or
    // a Referer header.
    viewReceipt: builder.query<PublicReceipt, ReceiptTokenArgs>({
      query: ({ token }) => ({
        url: '/receipts/view',
        method: 'POST',
        body: { token },
      }),
      // Dropped as soon as the page lets go: never served stale, and the
      // token-keyed entry doesn't linger in the store.
      keepUnusedDataFor: 0,
    }),
    // Resolves to an object URL for the PDF (the caller revokes it); a Blob
    // itself isn't serialisable, so it can't sit in the store.
    downloadReceiptPdf: builder.mutation<string, ReceiptTokenArgs>({
      query: ({ token }) => ({
        url: '/receipts/pdf',
        method: 'POST',
        body: { token },
        responseHandler: async response =>
          response.ok ? URL.createObjectURL(await response.blob()) : null,
      }),
    }),
  }),
});

export const { useViewReceiptQuery, useDownloadReceiptPdfMutation } = PublicReceiptApi;
